import React, { useState, useRef, useEffect, useCallback } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import uploadDocumentIcon from "../../assets/upload-documents-icon.png";
import pdfIcon from "../../assets/pdf.png";
import deleteIcon from "../../assets/delete-icon.png";
import { ChevronDown, ChevronsUpDown, ChevronRight, Languages, Save } from 'lucide-react';
import { toggleCollapseFiles, updateWorkflowSessionState } from '../../store/reducers'; 
import { useLanguage, getUploadDocumentsTranslation } from '../../hooks/useLanguage';
// PDFs are now stored only in Redux state (localStorage disabled to avoid quota issues) import { storeMultiplePdfsInSessionStorage, removePdfByFileName } from '../../utils/pdfStorage';
import { selectActiveTabId } from '../../store/selectors/tabSelectors';
import { addWorkflowFiles, removeWorkflowFile, setWorkflowFiles, getWorkflowFiles, hasWorkflowFile } from '../../utils/workflowFileCache';

function UploadDocuments() {
    const dispatch = useDispatch();
    const { documents, uploadComplete, uploadedFiles, hasDocuments, collapseFiles, stepsCompleted } = useSelector((state) => state.documents);
    const extractedData = useSelector(state => state.documents.extractedData);
    const activeTabId = useSelector(selectActiveTabId);
    const activeWorkflowSessionId = useSelector((state) => state.workflowSessions?.activeSessionId);
    const uploadSectionCollapsedFromSession = useSelector(
        (state) => activeWorkflowSessionId && state.workflowSessions?.sessions?.[activeWorkflowSessionId]?.state?.uploadSectionCollapsed
    );
    const isUploadSectionCollapsed = activeWorkflowSessionId ? (uploadSectionCollapsedFromSession ?? false) : false;
    const prevStepsCompletedRef = useRef(stepsCompleted);
    
    const [isDragging, setIsDragging] = useState(false);
    const [isProcessing, setIsProcessing] = useState(false);
    const [error, setError] = useState('');
    const fileInputRef = useRef(null);
    const [isSpanish] = useLanguage();
    const [localUploadSectionCollapsed, setLocalUploadSectionCollapsed] = useState(false);
    const effectiveCollapsed = activeWorkflowSessionId ? isUploadSectionCollapsed : localUploadSectionCollapsed;
    
    const setUploadSectionCollapsed = useCallback((value) => {
        if (activeWorkflowSessionId) {
            dispatch(updateWorkflowSessionState(activeWorkflowSessionId, { uploadSectionCollapsed: value }));
        } else {
            setLocalUploadSectionCollapsed(value);
        }
    }, [activeWorkflowSessionId, dispatch]);
    
    const MAX_DOCUMENTS = 20;

    const processFiles = async (files) => {
        try {
            setIsProcessing(true);
            setError('');
            
            // CRITICAL: Check for duplicates using the workflow cache (source of truth), not Redux Redux state might be stale when switching between workflows
            let existingFileNames = [];
            if (activeWorkflowSessionId) {
                const cachedFiles = getWorkflowFiles(activeWorkflowSessionId);
                existingFileNames = cachedFiles.map(file => file.name);
            } else {
                // Fallback to Redux if no workflow session
                existingFileNames = (uploadedFiles || []).map(file => file.name);
            }
            
            const newFiles = Array.from(files).filter(file => !existingFileNames.includes(file.name));
            const duplicateFiles = Array.from(files).filter(file => existingFileNames.includes(file.name));
            
            // if (duplicateFiles.length > 0) { const duplicateNames = duplicateFiles.map(f => f.name).join(', '); setError(`File(s) already exist: ${duplicateNames}`); }
            
            if (newFiles.length === 0) {
                setIsProcessing(false);
                return;
            }
            
            // Get current count from cache (source of truth) for accurate limit checking
            let currentCount = 0;
            if (activeWorkflowSessionId) {
                const cachedFiles = getWorkflowFiles(activeWorkflowSessionId);
                currentCount = cachedFiles.length;
            } else {
                currentCount = uploadedFiles ? uploadedFiles.length : 0;
            }
            
            const newFilesCount = newFiles.length;
            
            if (currentCount + newFilesCount > MAX_DOCUMENTS) {
                setError(getUploadDocumentsTranslation('maxDocumentsError', isSpanish, MAX_DOCUMENTS, newFilesCount, currentCount));
                setIsProcessing(false);
                return;
            }
            
            // Store PDFs in both Redux state AND per-workflow cache CRITICAL: Use cache as source of truth, then sync to Redux
            let allFiles = [];
            
            if (activeWorkflowSessionId) {
                // Add new files to cache (handles duplicates automatically)
                addWorkflowFiles(activeWorkflowSessionId, newFiles);
                
                // Get the complete, deduplicated list from cache (source of truth)
                allFiles = getWorkflowFiles(activeWorkflowSessionId);
                
                // Update Redux to match cache (cache is source of truth) CRITICAL: Order matters - set files first, then set uploadComplete This ensures Redux state is properly updated and triggers re-renders Note: CLEAR_DOCUMENTS resets uploadComplete to false, so we need to set it back to true
                dispatch({ type: 'CLEAR_DOCUMENTS' }); // This resets uploadComplete to false
                dispatch({ type: 'SET_UPLOADED_FILES', payload: allFiles });
                dispatch({ type: 'ADD_DOCUMENTS', payload: allFiles });
                dispatch({ type: 'UPLOAD_COMPLETE' }); // Set uploadComplete back to true
            } else {
                // Fallback if no workflow session (shouldn't happen, but handle gracefully)
                allFiles = [...uploadedFiles || [], ...newFiles];
                dispatch({ type: 'ADD_DOCUMENTS', payload: newFiles });
                dispatch({ type: 'SET_UPLOADED_FILES', payload: allFiles });
                dispatch({ type: 'UPLOAD_COMPLETE' });
            }
            
            // Store only file metadata in workflow session state (not File objects - they get serialized)
            if (activeWorkflowSessionId && allFiles.length > 0) {
                const fileMetadata = allFiles.map(file => ({
                    name: file.name,
                    size: file.size,
                    type: file.type || 'application/pdf'
                }));
                dispatch(updateWorkflowSessionState(activeWorkflowSessionId, {
                    documents: fileMetadata,
                    uploadedFiles: fileMetadata,
                    hasDocuments: true,
                    uploadComplete: true,
                }));
            }
            
        } catch (error) {
            console.error("Error processing files:", error);
            setError(error.message || getUploadDocumentsTranslation('errorProcessingFiles', isSpanish));
        } finally {
            setIsProcessing(false);
        }
    };

    const handleFileChange = (event) => {
        const selectedFiles = event.target.files;
        if (selectedFiles && selectedFiles.length > 0) {
            processFiles(selectedFiles);
        }
    };

    const handleDrop = (event) => {
        event.preventDefault();
        setIsDragging(false);
        const droppedFiles = event.dataTransfer.files;
        if (droppedFiles && droppedFiles.length > 0) {
            // Filter to only accept PDF files
            const pdfFiles = Array.from(droppedFiles).filter(file => file.type === 'application/pdf');
            if (pdfFiles.length > 0) {
                processFiles(pdfFiles);
            } else {
                setError(getUploadDocumentsTranslation('errorProcessingFiles', isSpanish));
            }
        }
    };

    const handleDragEnter = (event) => {
        event.preventDefault();
        setIsDragging(true);
    };

    const handleDragLeave = (event) => {
        event.preventDefault();
        setIsDragging(false);
    };

    const handleDelete = async (fileName) => {
        // Remove from per-workflow cache FIRST
        if (activeWorkflowSessionId) {
            removeWorkflowFile(activeWorkflowSessionId, fileName);
        }
        
        const updatedDocuments = documents.filter(file => file.name !== fileName);
        const updatedUploadedFiles = uploadedFiles.filter(file => file.name !== fileName);
        const hasFilesLeft = updatedUploadedFiles.length > 0;
        
        // Update Redux state (UploadDocuments reads from Redux)
        dispatch({ type: 'REMOVE_DOCUMENT', payload: fileName });
        dispatch({ type: 'SET_UPLOADED_FILES', payload: updatedUploadedFiles });
        
        // Ensure cache is in sync with Redux
        if (activeWorkflowSessionId) {
            setWorkflowFiles(activeWorkflowSessionId, updatedUploadedFiles);
        }
        
        if (!hasFilesLeft) {
            dispatch({ type: 'RESET_UPLOAD_STATUS' });
        }
        
        // Update workflow session state with metadata only
        if (activeWorkflowSessionId) {
            const fileMetadata = updatedUploadedFiles.map(file => ({
                name: file.name,
                size: file.size,
                type: file.type || 'application/pdf'
            }));
            dispatch(updateWorkflowSessionState(activeWorkflowSessionId, {
                documents: fileMetadata,
                uploadedFiles: fileMetadata,
                hasDocuments: hasFilesLeft,
                uploadComplete: hasFilesLeft,
            }));
        }
        
        setError('');
    };

    const formatSizeInMB = (sizeInBytes) => {
        // Safety check: ensure sizeInBytes is a valid number
        if (!sizeInBytes || typeof sizeInBytes !== 'number' || isNaN(sizeInBytes) || sizeInBytes <= 0) {
            return '0.00';
        }
        return (sizeInBytes / (1024 * 1024)).toFixed(2);
    };
    
    // Auto-remove error message after 10 seconds
    useEffect(() => {
        if (error) {
            const timer = setTimeout(() => {
                setError('');
            }, 1000000); // 10 seconds
            
            return () => clearTimeout(timer);
        }
    }, [error]);

    // Auto-collapse when stream is finished (stepsCompleted becomes true); persist in session so it stays collapsed after remount
    useEffect(() => {
        const justCompleted = !prevStepsCompletedRef.current && stepsCompleted;
        prevStepsCompletedRef.current = stepsCompleted;
        if (justCompleted && extractedData && activeWorkflowSessionId) {
            setUploadSectionCollapsed(true);
        }
    }, [stepsCompleted, extractedData, activeWorkflowSessionId, setUploadSectionCollapsed]);

    const remainingDocuments = MAX_DOCUMENTS - (uploadedFiles ? uploadedFiles.length : 0);
    
    const isUploadDisabled = remainingDocuments === 0 || isProcessing || extractedData;
    // Show collapse button when we have extracted data or when section is already collapsed
    const showCollapseButton = !!extractedData || effectiveCollapsed;
    
    const getButtonText = () => {
        if (isProcessing) return getUploadDocumentsTranslation('processing', isSpanish);
        if (extractedData) return getUploadDocumentsTranslation('documentsProcessed', isSpanish);
        if (remainingDocuments === 0) return getUploadDocumentsTranslation('documentLimitReached', isSpanish);
        return getUploadDocumentsTranslation('selectFiles', isSpanish);
    };
    
    const getMessageText = () => {
        if (extractedData) return getUploadDocumentsTranslation('documentsSuccessfullyProcessed', isSpanish);
        if (remainingDocuments === 0) return getUploadDocumentsTranslation('pleaseRemoveSome', isSpanish);
        return null;
    };

    return (
        <div className="flex-1 flex flex-col">
            <div className="mb-4">
                <div className="circle bg-default mb-2">
                    <span className="text-white">1</span>
                </div>
                <div className={`flex items-center justify-between gap-4 ${showCollapseButton ? 'w-fit gap-4' : 'w-full'}`}>
                    <h1 className="text-3xl font-bold mb-0">{getUploadDocumentsTranslation('uploadDocuments', isSpanish)}</h1>
                    {showCollapseButton && (
                        <button
                            type="button"
                            onClick={() => setUploadSectionCollapsed(!effectiveCollapsed)}
                            className={`flex-shrink-0 p-1 rounded-full bg-gray-100 transition-colors ${effectiveCollapsed ? 'mt-1' : ''}`}
                            title={effectiveCollapsed ? (isSpanish ? 'Expandir' : 'Expand') : (isSpanish ? 'Colapsar' : 'Collapse')}
                            aria-expanded={!effectiveCollapsed}
                        >
                            {effectiveCollapsed ? (
                                <ChevronRight className="size-5 text-gray-600" />
                            ) : (
                                <ChevronDown className="size-5 text-gray-600" />
                            )}
                        </button>
                    )}
                </div>
                {!effectiveCollapsed && (
                    <p className="pr-8 text-gray-500 mt-2 mb-0">
                        {getUploadDocumentsTranslation('uploadDescription', isSpanish)}
                    </p>
                )}
            </div>
            {!effectiveCollapsed && (
            <div className="flex-col border-container p-4 rounded-xl flex-1 flex">
                <div
                    className={`flex flex-col justify-center flex-1 text-center border-dashed border-2 border-gray-400 p-8 rounded-md ${isDragging ? 'bg-secondary' : 'bg-white'} ${isUploadDisabled ? 'opacity-50' : ''}`}
                    onDrop={!isUploadDisabled ? handleDrop : (e) => e.preventDefault()}
                    onDragOver={(e) => e.preventDefault()}
                    onDragEnter={!isUploadDisabled ? handleDragEnter : (e) => e.preventDefault()}
                    onDragLeave={handleDragLeave}
                >
                    <input 
                        type="file" 
                        onChange={handleFileChange} 
                        className="hidden" 
                        accept="application/pdf"
                        multiple
                        ref={fileInputRef}
                        disabled={isUploadDisabled}
                    />
                    <button
                        onClick={() => !isUploadDisabled && fileInputRef.current.click()}
                        className={`select-file-button my-0 mx-auto py-4 px-8 rounded-full flex gap-4 items-center m-auto ${!isUploadDisabled ? 'bg-default' : extractedData ? 'bg-default' : 'bg-gray-400'}`}
                        disabled={isUploadDisabled}
                    >
                        <img src={uploadDocumentIcon} className="h-6 brightness-0 invert" alt="Upload icon" />
                        <p className="font-bold text-white truncate">
                            {getButtonText()}
                        </p>
                    </button>
                    
                    {!isUploadDisabled ? (
                        <>
                            <p className="mt-4 opacity-50">{getUploadDocumentsTranslation('or', isSpanish)}</p>
                            <p className="mt-4 truncate">{getUploadDocumentsTranslation('dragDrop', isSpanish)}</p>
                        </>
                    ) : (
                        <p className={`mt-4 ${extractedData ? 'text-default' : 'text-red-500'}`}>
                            {getMessageText()}
                        </p>
                    )}
                </div>

                {error && (
                    <div className="mt-4 p-3 bg-red-100 text-red-700 rounded-md">
                        {error}
                    </div>
                )}
                
                {uploadComplete && uploadedFiles && uploadedFiles.length > 0 && (
                    <div className="mt-4 overflow-auto">
                        <div className='flex items-center'>
                        <h2 className="text-xl font-bold mb-2">{getUploadDocumentsTranslation('uploadedFiles', isSpanish)}
                        <span className="text-sm text-gray-600 ml-2">
                        ({uploadedFiles ? uploadedFiles.length : 0}/{MAX_DOCUMENTS})
                    </span>
                        </h2>
                        <button
                          className={`flex items-center gap-2 py-2 px-2 ml-4 rounded-full hover:bg-secondary w-fit`}
                          onClick={() => dispatch(toggleCollapseFiles())}
                        >
                        <ChevronDown className={`size-5`} />
                        </button>
                        </div>
                        {!collapseFiles && (
                        <table className="min-w-full border-collapse text-sm">
                            <thead>
                                <tr>
                                    <th className="p-2 pl-0">{getUploadDocumentsTranslation('fileName', isSpanish)}</th>
                                    <th className="p-2">{getUploadDocumentsTranslation('size', isSpanish)}</th>
                                    <th className="p-2 text-center"></th>
                                </tr>
                            </thead>
                            <tbody>
                                {uploadedFiles.map((file, index) => (
                                    <tr key={index} className="truncate">
                                        <td className="p-2 pl-0 truncate">
                                            <div className="flex gap-2 items-center">
                                                <img src={pdfIcon} className="h-8 w-8" alt="PDF icon" />
                                                <p className="truncate">{file.name}</p>
                                            </div>
                                        </td>
                                        <td className="p-2 truncate">{formatSizeInMB(file.size)}{getUploadDocumentsTranslation('mb', isSpanish)}</td>
                                        {hasDocuments && uploadComplete && !extractedData && (
                                        <td className="p-2">
                                            <button
                                                onClick={() => handleDelete(file.name)}
                                                className="text-red-500 m-auto flex h-6 w-6"
                                                title={getUploadDocumentsTranslation('delete', isSpanish)}
                                            >
                                                <img src={deleteIcon} className="h-6 w-6" alt={getUploadDocumentsTranslation('delete', isSpanish) + ' icon'} />
                                            </button>
                                        </td>
                                        )}
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                        )}
                    </div>
                )}
            </div>
            )}
        </div>
    );
}

export default UploadDocuments;